import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Redux & Services
import * as actionLoading from '../../Redux/Loading/LoadingActions';
import AdminService from "../../Services/AdminService";

// Core Layout UI
import { ButtonBlack } from "../../Utils/Buttons/button-component";
import Layout from "../../Utils/Navbar/navbar-layout-component";
import Footer from "../../Utils/Footer/footer-component";
import { Modal } from "../../Utils/Modal/modal-component";
import { LoadingAnimation } from "../../Utils/Progress/progress-component";
import Toast from "../../Utils/Toast/toast-component";
import SkeletonBuilder from "../../Utils/Skeleton/skeleton-builder-component";
import { getEditorStrings } from "../../Public/editor-screen-data";

// Workstation Components
import SchemaEditorModal from "../../Utils/Modal/modal-scheme-editor";
import EntryCard from "../../Utils/Cards/card-entiry-component";

// ==========================================
// 1. UTILITIES
// ==========================================
export const isLikelyImageKey = (k) => /(^|_)(image|media|img|icon|cover|photo|video)(s)?$/i.test(k);
export const isMultiline = (s) => typeof s === 'string' && (s.length > 70 || /\n/.test(s));

export function inferSchemaFromSample(sample) {
    if (!sample || typeof sample !== 'object') return [];
    return Object.keys(sample).map((key) => {
        if (key === 'id') return null;
        const val = sample[key];
        if (Array.isArray(val)) return { key, label: key, type: "array" };
        if (typeof val === 'boolean') return { key, label: key, type: "boolean" };
        if (typeof val === 'number') return { key, label: key, type: "number" };
        if (typeof val === 'string') {
            if (isLikelyImageKey(key)) return { key, label: key, type: "image" };
            if (isMultiline(val)) return { key, label: key, type: "textarea" };
            return { key, label: key, type: "text" };
        }
        if (typeof val === 'object' && val !== null) return { key, label: key, type: "object" };
        return { key, label: key, type: "text" };
    }).filter(Boolean);
}

export function buildEmptyBySchema(schema) {
    const obj = {};
    schema.forEach(({ key, type }) => {
        switch (type) {
            case "boolean": obj[key] = true; break;
            case "array": obj[key] = []; break;
            case "object": obj[key] = {}; break;
            default: obj[key] = "";
        }
    });
    return obj;
}

// ==========================================
// 2. HEADER BAR: Fully localized
// ==========================================
const HeaderBar = ({ field, onAddNew, itemCount, onBack, strings, dir }) => (
    <div className="w-full flex flex-col md:flex-row justify-between items-end mb-12 border-b border-neutral-100 dark:border-neutral-800 pb-8 gap-6">
        <div className="flex flex-col gap-2">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-md font-bold text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors uppercase mb-2"
            >
                {dir === 'rtl' ? '←' : '→'} {strings.header.back}
            </button>
            <div className="flex items-center gap-4">
                <h1 className="text-4xl font-black text-neutral-900 dark:text-white  capitalize">
                    {field}
                </h1>
                <span className="px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-[10px] font-bold text-neutral-500 uppercase ">
                    {itemCount} {strings.card.itemsCount}
                </span>
            </div>
            <p className="text-neutral-500 font-medium text-sm max-w-md">
                {strings.header.manageContent}
            </p>
        </div>

        <div className="flex items-center gap-3">
            <ButtonBlack onClick={onAddNew} className="!px-8 !py-3 !rounded-xl !text-xs font-bold shadow-xl active:scale-95 transition-all">
                {strings.header.addNew}
            </ButtonBlack>
        </div>
    </div>
);

// ==========================================
// 3. MAIN EDITOR SCREEN
// ==========================================
const EditorScreen = () => {
    const { Field } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const systemData = useSelector(s => s.systemData || {});
    const { language, dir } = systemData;
    const t = getEditorStrings(language);
    const loading = useSelector(s => s.loading);

    const [dataState, setDataState] = useState([]);
    const [skeleton, setSkeleton] = useState(true);
    const [modal, setModal] = useState(false);
    const [schema, setSchema] = useState([]);
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState({});
    const [refresh, setRefresh] = useState(0);


    useEffect(() => {
        const fetch = async () => {
            window.scrollTo(0, 0, 'smooth');
            dispatch(actionLoading.setLoading(t.loading.fetching));
            try {
                const data = await AdminService.getSystemDataByPath(Field);
                const normalized = Array.isArray(data) ? data : (data && typeof data === 'object' ? [data] : []);
                setDataState(normalized);

                if (normalized.length > 0) setSchema(inferSchemaFromSample(normalized[0]));
            } finally {
                dispatch(actionLoading.disableLoading());
                setSkeleton(false);
            }
        };
        fetch();
    }, [Field, refresh, dispatch, t.loading.fetching]);

    const primaryImg = useMemo(() => {
        const sample = dataState?.[0];
        return sample ? Object.keys(sample).find(isLikelyImageKey) : null;
    }, [dataState]);

    const handleSave = async (payload) => {
        setModal(false);
        dispatch(actionLoading.setLoading(editId ? t.loading.updating : t.loading.saving));
        try {
            if (editId) {
                payload.id = editId;
                await AdminService.updateSystemData(Field, payload);
            } else {
                await AdminService.addSystemDataEntry(Field, payload);
            }
            setRefresh(r => r + 1);
        } catch (e) {
            alert(t.alerts.saveFailed);
        } finally {
            dispatch(actionLoading.disableLoading());
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t.alerts.deleteConfirm)) return;
        if (dataState.length <= 1) return alert(t.alerts.minItems);

        dispatch(actionLoading.setLoading(t.loading.deleting));
        try {
            await AdminService.deleteSystemDataEntry(Field, id);
            setRefresh(r => r + 1);
        } finally {
            dispatch(actionLoading.disableLoading());
        }
    };

    return (
        <div dir={dir} className="selection:bg-blue-100 dark:selection:bg-blue-900/30">
            <Modal expand={loading.visible} timeout backdrop='bg-white/80 backdrop-blur-sm' popUp>
                <LoadingAnimation loadingMessage={loading.message} />
            </Modal>

            <Toast />

            <Layout>
                <main className="py-12 bg-white dark:bg-[#080808] min-h-screen">
                    <div className="max-w-7xl mx-auto px-6">
                        <HeaderBar
                            field={Field}
                            strings={t}
                            dir={dir}
                            itemCount={dataState.length}
                            onBack={() => navigate('/home')}
                            onAddNew={() => {
                                setEditId(null);
                                setDraft(buildEmptyBySchema(schema));
                                setModal(true);
                            }}
                        />

                        {dataState.length === 0 && !skeleton ? (
                            <div className="w-full py-20 text-center border-2 border-dashed border-neutral-100 dark:border-neutral-800 rounded-3xl">
                                <p className="text-neutral-400 font-bold uppercase italic">
                                    {t.emptyState}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                                {skeleton ? (
                                    Array(4).fill(0).map((_, i) => <SkeletonBuilder key={i} />)
                                ) : (
                                    dataState.map(entry => (
                                        <EntryCard
                                            key={entry.id}
                                            entry={entry}
                                            primaryImageKey={primaryImg}
                                            strings={t}
                                            disableDelete={dataState.length <= 1}
                                            onEdit={e => {
                                                setEditId(e.id);
                                                setDraft(JSON.parse(JSON.stringify(e)));
                                                setModal(true);
                                            }}
                                            onClone={e => {
                                                const clone = JSON.parse(JSON.stringify(e));
                                                delete clone.id;
                                                setEditId(null);
                                                setDraft(clone);
                                                setModal(true);
                                            }}
                                            onDelete={handleDelete}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </Layout>
            <Footer />

            <SchemaEditorModal
                open={modal}
                onClose={() => setModal(false)}
                schema={schema}
                draft={draft}
                setDraft={setDraft}
                onSave={handleSave}
                strings={t}
            />
        </div>
    );
};

export default EditorScreen;